import { useEffect, memo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import useAdminTableState from '../../hooks/useAdminTableState'
import AdminTableControls from './AdminTableControls'
import { formatDate } from '../../utils/dateFormatting'

const AdminUsersSection = memo(function AdminUsersSection({ onBack }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()
  const [selectedIds, setSelectedIds] = useState(new Set())

  const {
    data: users,
    loading,
    error,
    searchInput,
    search,
    filter,
    pagination,
    handleSearchChange,
    handleFilterChange,
    handlePageChange,
    refetch
  } = useAdminTableState(
    async (params) => {
      return execute(async () => {
        // Hook provides params with: { page, limit, search?, filter? }
        const apiParams = { ...params }
        
        // Map filter to role/status
        if (params.filter === 'admin') {
          apiParams.role = 'admin'
          delete apiParams.filter
        } else if (params.filter === 'user') {
          apiParams.role = 'user'
          delete apiParams.filter
        } else if (params.filter === 'active') {
          apiParams.status = 'active'
          delete apiParams.filter
        } else if (params.filter === 'inactive') {
          apiParams.status = 'inactive'
          delete apiParams.filter
        } else if (params.filter && params.filter !== 'all') {
          delete apiParams.filter
        }
        
        const response = await adminAPI.getUsers(apiParams)
        return {
          data: response.data?.data || [],
          pagination: {
            page: response.data?.pagination?.page || 1,
            totalPages: response.data?.pagination?.totalPages || 1,
            total: response.data?.pagination?.total || 0
          }
        }
      })
    },
    { pageSize: 20 }
  )

  const filterOptions = [
    { value: 'all', label: 'All Users' },
    { value: 'admin', label: 'Admins' },
    { value: 'user', label: 'Regular Users' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ]

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(users.map(u => u.id)))
    } else {
      setSelectedIds(new Set())
    }
  }, [users])

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Are you sure you want to delete ${selectedIds.size} user(s)?`)) return

    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => adminAPI.deleteUser(id)))
      setSelectedIds(new Set())
      refetch()
    }, { successMessage: `${selectedIds.size} user(s) deleted successfully` })
  }, [selectedIds, refetch, execute])

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Users Management</h2>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/admin/users/new')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 text-sm font-medium"
          >
            + Create User
          </button>
          <button
            onClick={onBack}
            className="text-sm text-primary hover:text-primary-600"
          >
            ← Back to Overview
          </button>
        </div>
      </div>

      <AdminTableControls
        searchValue={searchInput}
        searchPlaceholder="Search by name, email, or phone..."
        onSearchChange={handleSearchChange}
        filterValue={filter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemLabel="users"
        onPageChange={handlePageChange}
      />

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="px-4 lg:px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedIds.size} user(s) selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 text-sm bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 lg:px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={users.length > 0 && selectedIds.size === users.length}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-4 lg:px-6 py-8 text-center text-gray-500">{t('app.loading')}</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="6" className="px-4 lg:px-6 py-8 text-center text-red-500">{error}</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 lg:px-6 py-8 text-center text-gray-500">No users found</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr 
                  key={user.id} 
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                  className={`cursor-pointer hover:bg-gray-50 ${selectedIds.has(user.id) ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.id)}
                      onChange={() => handleSelectOne(user.id)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.phone || 'N/A'}</div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.isAdmin ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.isAdmin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
})

export default AdminUsersSection
